// import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import React, { ReactElement, Suspense, useEffect, useState } from 'react';
import ReactGA from 'react-ga';
import { Outlet, useLocation } from 'react-router-dom';
import Center from 'src/components/Center';
import CodeusErrorBoundary from 'src/components/CodeusErrorBoundary';
import FullPageError from 'src/components/FullPageError';

import TermsAndConditions from 'src/components/TermsAndConditions';
import FEATURES from 'src/featureMap';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import LoadingIndicator from 'src/lib/components/LoadingIndicator';
import BaseLayout from 'src/routes/layouts/BaseLayout';
import ulog from 'ulog';
import { version } from '../../package.json';
import { BDSProvider } from '@barracuda-internal/bds-core-v2';

/**
 * This component handles:
 * - Anything that needs to run ON EVERY URL CHANGE
 * - App-level error handling via Sentry
 */
export let storageRegion = '';
function AppRoute(): ReactElement {
	const { api } = useAppToolchain();
	const [doTermsAccepted, setDoTermsAccepted] = useState(false);
	const { auth } = useAuth();

	const location = useLocation();

	ulog.debug(`APP: Page changed to ${location.pathname}`);

	useEffect(() => {
		const action = FEATURES.pageChange;
		mixpanel.track(`DIN ${action.id}`, {
			urlPath: location.pathname,
			feature: action.feature,
			appVersion: version,
		});

		if (window?.ga) {
			ReactGA.pageview(location.pathname);
		}
	}, [location]);

	useEffect(() => {
		if (window?.Intercom)
			window.Intercom('update', {
				last_request_at: new Date().getTime() / 1000,
			});
	}, []);

	const [userReqStatus, user, error] = useApiRequest(
		(signal) => api.getLoggedInUser({ signal }),
		[doTermsAccepted],
	);

	useApiRequest(() => {
		if (!auth) return;
		return api
			.getAccountConfig(auth?.accountDetails.account.id)
			.then((res) => {
				storageRegion = res.storageRegion;
			});
	}, [auth]);

	let content: React.ReactNode;

	if (userReqStatus !== 'RESOLVED') {
		content = (
			<BaseLayout>
				<Center>
					<div>
						{error && error?.status !== 401 ? (
							<FullPageError message="There was an error loading the application." />
						) : (
							<LoadingIndicator
								message="Loading..."
								loaderSize={100}
							/>
						)}
					</div>
				</Center>
			</BaseLayout>
		);
	} else if (user && !user?.config?.termsAgreeTime) {
		content = (
			<TermsAndConditions onAccepted={() => setDoTermsAccepted(true)} />
		);
	} else {
		content = (
			<Suspense
				fallback={
					<LoadingIndicator message="Loading..." loaderSize={100} />
				}
			>
				<Outlet />
			</Suspense>
		);
	}

	return (
		// <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
		// 	<BDSProvider useBdsTheme={true}>{content}</BDSProvider>
		// </Sentry.ErrorBoundary>
		<CodeusErrorBoundary>
			<BDSProvider useBdsTheme={true}>{content}</BDSProvider>
		</CodeusErrorBoundary>
	);
}

export default AppRoute;
